/**
 * Step 4 living-arrangement insight knowledge.
 * AgedCareWise models financial outcomes (cashflow, Age Pension, legacy) only.
 */
import type { FactFind, FactFindGoals } from '../../../shared/src/calculation/factFindTypes.ts';
import { EXTRA_ALLOWABLE_AMOUNT } from '../../../shared/src/calculation/constants/ratesAndThresholds.ts';

/** The extra allowable amount, from the one constant, so this copy never goes stale (8 Oct 2026). */
const EAA_TEXT = '$' + Math.floor(EXTRA_ALLOWABLE_AMOUNT).toLocaleString('en-AU');
import { buildHomeLandInsightSection } from './homeLandInsight.ts';
import { buildVillageValueInsightSection } from './villageValueInsight.ts';
import { partnerRemainsInFamilyHome } from '../../../shared/src/calculation/householdStatus.ts';
import type { DraftFactFind } from './initialFactFind.ts';

interface GoalTheme {
    label: string;
    summary: string;
    summaryNonHomeowner?: string;
}

export const STEP4_GOAL_THEMES: Record<string, GoalTheme> = {
    home_care: {
        label: 'Support at Home',
        summary:
            'Your expected contributions toward home care, cashflow after those costs, and scenarios where you change the home (for example downsizing to free up money for care).',
        summaryNonHomeowner:
            'Your expected contributions toward home care and your cashflow after those costs.',
    },
    aged_care: {
        label: 'Residential aged care',
        summary: 'Selling or keeping the home, renting it, accommodation payments, and who remains in the home.',
        summaryNonHomeowner:
            'Accommodation payments (a refundable deposit and/or a daily payment), the means-tested care fees, and your ongoing cashflow.',
    },
    downsize_home: {
        label: 'Downsizing',
        summary: 'Proceeds from the sale and the cost of your next home.',
        summaryNonHomeowner:
            'Modelled if you own a home to sell — the sale proceeds and the cost of your next home.',
    },
    retirement_village: {
        label: 'Retirement village',
        summary: 'Entry, ongoing and exit costs for village living.',
    },
    granny_flat: {
        label: 'Granny flat',
        summary: 'Payments or assets transferred for your right to live there.',
    },
};

/** Optional extra paragraph when exactly these two goals are selected (and no others). */
export const STEP4_TWO_GOAL_NOTES: Record<string, string> = {
    'home_care+aged_care':
        'Many people treat one option as the main plan and the other as a backup. We show the financial comparison for whichever scenarios you choose to run.',
    'home_care+downsize_home':
        'We can contrast staying in your current home with selling and moving, using the figures you enter.',
    'aged_care+downsize_home':
        'Often used when a partner stays behind or equity is released before care. We model the financial outcomes you set up.',
};

/**
 * Living-arrangement-specific insight content for the Step 4 right-hand panel.
 * Each arrangement surfaces the financial points that actually matter for it (pension homeowner status,
 * asset limits, Rent Assistance, deprivation/exit-fee traps) in plain language — see
 * docs/LIVING_ARRANGEMENTS_KNOWLEDGE_AND_FACTFIND.md.
 */
interface ResidencyInsight {
    title: string;
    subtitle: string;
    paragraphs: string[];
}

export const STEP4_RESIDENCY_INSIGHTS: Record<string, ResidencyInsight> = {
    /*
     * THE PANEL EXPLAINS; THE FORM REPORTS (owner, 8 Oct 2026). Each card says what Centrelink does
     * with the arrangement and what the module does with it — once, here. Under the fields only
     * CONSEQUENCES appear: a gift that arose or aged out, a village unit's fate once answered. The
     * general "How this is assessed" and "What this means" boxes that used to repeat these cards
     * beneath the fields are gone.
     */
    own_home: {
        title: 'Your family home',
        subtitle: 'Homeowner',
        paragraphs: [
            'Your home itself isn’t counted in the Age Pension assets test — its value doesn’t matter. Being a homeowner does mean your other assets (savings, super, investments) are assessed against a lower threshold than someone who doesn’t own a home.',
            'If you later move into care, downsize, or rent it out, the way your home is treated changes — we model each of those paths from the details you enter.',
        ],
    },
    renting: {
        title: 'Renting your home',
        subtitle: 'Non-homeowner',
        paragraphs: [
            'As a renter you don’t own a home, so you’re assessed under the higher non-homeowner asset limits — you can hold more in assets before your pension reduces. The rent you pay is counted in your cashflow.',
            'If you get the Age Pension and pay private rent, you may also receive Commonwealth Rent Assistance on top of your pension, which we include. It isn’t paid in public housing, or usually when you rent from a family member you live with.',
        ],
    },
    with_family: {
        title: 'Living with family',
        subtitle: 'Non-homeowner',
        paragraphs: [
            'Living with family without owning the home, you’re generally assessed under the higher non-homeowner asset limits. Any board you pay is counted in your cashflow.',
            'If you sold a home to move in, what is left of the proceeds belongs under Assets as it stands today. Anything you gave to family belongs under Gifting with its date — the five-year rule is applied there.',
            'If you paid money or transferred assets for the right to live there, that is a granny flat arrangement with its own treatment: answer the question and the right fields appear.',
        ],
    },
    granny_flat: {
        title: 'Granny flat arrangement',
        subtitle: 'Centrelink homeowner test applies',
        paragraphs: [
            'A granny flat arrangement gives you a lifetime right to live there in exchange for money or assets you transferred. If what you paid is above the “extra allowable amount” (' + EAA_TEXT + ') you are treated as a homeowner.',
            'Signing over the home you lived in, paying for the flat to be built, or buying a property in the family’s name is taken at face value as the price of the right, whatever it is worth — no part of it is a gift. Centrelink’s “reasonableness test” applies to cash, and to another property or other assets given on top: anything above the assessed value of the right is a gift for five years from the date the arrangement started. We work that out from your figures and show the result under the fields.',
            'Two things worth settling with a solicitor: a written agreement, which makes your right to live there enforceable if circumstances change (the family member divorces, passes away, sells, or has financial trouble) and is what Centrelink looks for; and fairness between your children, since money or property given to one can leave the others worse off and is a common cause of family disputes. Both are worth reflecting in your will.',
        ],
    },
    retirement_village: {
        title: 'Retirement village',
        subtitle: 'Homeowner status depends on your entry contribution',
        paragraphs: [
            'In a retirement village you usually hold a lease or licence, not the title. Centrelink compares your entry contribution to ' + EAA_TEXT + ': above it you’re treated as a homeowner (the contribution is exempt); at or below it as a non-homeowner (higher asset limits, and you may get Rent Assistance on the ongoing fees). Your ongoing fees are counted in your cashflow.',
            'Where the goal is residential care, the exit terms you enter let us work out what the village pays back when you leave, when, and what the wait costs. For a couple where only one is moving, what happens to the unit decides whether anything is paid out at all.',
        ],
    },
    aged_care: {
        title: 'Residential aged care',
        subtitle: 'Already in care',
        paragraphs: [
            'For residential care we model your accommodation payment (a refundable lump sum, a daily payment, or a mix), the means-tested care fees, and the basic daily fee.',
            'If you are looking to move to another home, we compare staying put with the move: the deposit comes back within 14 days, less any retention taken so far, and goes to the new home’s room first; the fees switch to the new home’s on the day of the move, and the yearly and lifetime caps carry on. Moving in before 1 November 2025 keeps the earlier fee rules.',
            'How your former home is treated — kept, rented, or sold, and whether a “protected person” still lives there — changes both your pension and your care fees. We model each option.',
        ],
    },
};

export const STEP4_FIELD_TOOLTIPS: Record<string, string> = {
    homeGrowthRate:
        'Assumed growth for long-term equity and legacy projections — not a guarantee.',
    likelyRent:
        'Used if you model keeping and renting the home (for example, while in care).',
    likelyExpenses:
        'Annual costs if the home is rented (rates, insurance, management, maintenance). Use Calculate to estimate.',
};

const LIVING_GOAL_KEYS: (keyof FactFindGoals)[] = ['home_care', 'aged_care', 'retirement_village', 'granny_flat', 'downsize_home'];

const TWO_GOAL_PAIRS: (keyof FactFindGoals)[][] = [
    ['home_care', 'aged_care'],
    ['home_care', 'downsize_home'],
    ['aged_care', 'downsize_home'],
];

export function getSelectedLivingGoals(goals: FactFindGoals = {}): (keyof FactFindGoals)[] {
    return LIVING_GOAL_KEYS.filter((key) => goals[key]);
}

export function formatGoalList(selectedGoals: (keyof FactFindGoals)[]): string {
    const labels = selectedGoals.map((g) => STEP4_GOAL_THEMES[g]?.label).filter((label): label is string => Boolean(label));
    if (labels.length === 0) return '';
    if (labels.length === 1) return labels[0];
    if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
    return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

function getTwoGoalNoteKey(selectedGoals: (keyof FactFindGoals)[]): string | null {
    if (selectedGoals.length !== 2) return null;
    for (const pair of TWO_GOAL_PAIRS) {
        if (pair.every((g) => selectedGoals.includes(g))) return pair.join('+');
    }
    return null;
}

/**
 * Which partner is the one staying in the home — the one NOT entering residential care.
 *
 * Only called when `partnerRemainsInFamilyHome` is already true, so exactly one of the two is headed
 * for care. Falls back to "Your partner" rather than a blank when the name has not been entered yet.
 */
function whoStaysInTheHome(factFind: DraftFactFind): string {
    const clientEnteringCare = (factFind.goals?.aged_care_for ?? 'client') !== 'partner';
    const stayingFirstName = clientEnteringCare ? factFind.partner?.firstName : factFind.client?.firstName;
    return String(stayingFirstName || '').trim() || 'Your partner';
}

export function buildStep4InsightSections({ factFind }: { factFind: DraftFactFind }) {
    let residency = factFind.client?.currentResidency || 'own_home';
    // Derived: "living with family" + paid for a right to live there → treat as a granny-flat arrangement.
    if (residency === 'with_family' && factFind.livingWithFamily?.grannyFlatInterest === true) {
        residency = 'granny_flat';
    }
    const selectedGoals = getSelectedLivingGoals(factFind.goals || {});
    const insight = STEP4_RESIDENCY_INSIGHTS[residency] || STEP4_RESIDENCY_INSIGHTS.own_home;

    // Always lead with the arrangement-specific content so the panel reflects the client's actual situation.
    const paragraphs = [...insight.paragraphs];

    // The home being exempt is the single biggest thing driving the means-tested care fee, so it is
    // said out loud — but here, in the panel that EXPLAINS things, not in the form as a question. It
    // used to be a dropdown ("will someone remain living there?") sitting directly beneath the "what
    // rent could you get for it?" fields, which read as a contradiction and invited the wrong answer.
    // Nobody has to answer it: it follows from who the care goal is for, and moves when that moves.
    if (partnerRemainsInFamilyHome(factFind as unknown as FactFind)) {
        const stayingName = whoStaysInTheHome(factFind);
        paragraphs.push(
            `${stayingName} will still be living in the home, which makes it exempt from the aged care means test for as long as that is the case. ` +
                `That exemption is often the largest single factor in the care fee. It comes from the care goals you set in Step 3, so if those change, this changes with them.`,
        );
    }

    // The goals chosen are the card's subtitle; a sentence repeating them said nothing (owner, 8 Oct
    // 2026). What stays is the note for a pair of goals that are usefully compared side by side.
    if (selectedGoals.length === 0) {
        paragraphs.push('Select your goals in Step 3 to see what we include in your projections.');
    } else {
        const twoGoalKey = getTwoGoalNoteKey(selectedGoals);
        if (twoGoalKey && STEP4_TWO_GOAL_NOTES[twoGoalKey]) {
            paragraphs.push(STEP4_TWO_GOAL_NOTES[twoGoalKey]);
        }
    }

    // Goal summaries that reference "the home" only make sense for homeowners. For every other living
    // arrangement, use the non-homeowner wording so the panel doesn't talk about selling/downsizing a home.
    const isHomeownerArrangement = residency === 'own_home';
    const goalBlocks =
        selectedGoals.length > 1
            ? selectedGoals.map((key) => {
                  const theme = STEP4_GOAL_THEMES[key];
                  const summary =
                      !isHomeownerArrangement && theme.summaryNonHomeowner
                          ? theme.summaryNonHomeowner
                          : theme.summary;
                  return { label: theme.label, summary };
              })
            : [];

    // Land the home exemption does not cover gets its own card, below the arrangement one. Its own
    // card rather than another paragraph because it is a DIFFERENT subject: the paragraphs above are
    // about where the client lives, this is about an asset that changes their pension and their care
    // fee. It appears only once they have answered one of the land questions yes.
    const landSection = buildHomeLandInsightSection(factFind);
    // A village resident's exit entitlement, line by line, live from the fields (8 Oct 2026).
    const villageSection = buildVillageValueInsightSection(factFind);

    return [
        {
            title: insight.title,
            subtitle: selectedGoals.length > 0 ? formatGoalList(selectedGoals) : insight.subtitle,
            paragraphs,
            goalsIntro:
                goalBlocks.length > 0
                    ? 'Each goal adds different details to the projections above:'
                    : null,
            goalBlocks,
        },
        ...(villageSection ? [villageSection] : []),
        ...(landSection ? [landSection] : []),
    ];
}
